<script setup lang="ts">
  import { computed, ref, useTemplateRef } from 'vue'
  import { testIds } from '@gat-ship/types'
  import {
    Button,
    ButtonGroup,
    ButtonGroupSeparator,
    cn,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    Modal,
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
    Separator,
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarHeader,
    SidebarInset,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarProvider
  } from '@gat-ship/vue-components'
  import {
    IconBan,
    IconClose,
    IconDotsHorizontal,
    IconMaximize,
    IconMinimize
  } from '@gat-ship/icons'
  import { useAgencyContext } from '../../composables/useAgency'
  import {
    getAgencyDetailSidebarItems,
    type AgencyDetailSection
  } from '../detail-modal/agency-detail-modal-sections'
  import AgencySelect from '../inputs/AgencySelect.vue'
  import AgencyModalProvider from '../providers/AgencyModalProvider.vue'
  import AgencyGeneralView from '../../views/AgencyGeneralView.vue'
  import AgencyPortsView from '../../views/AgencyPortsView.vue'
  import AgencyDangerZoneView from '../../views/AgencyDangerZoneView.vue'

  defineProps<{
    agencyId?: string
  }>()

  const open = defineModel<boolean>('open', { required: true })
  const activeSection = defineModel<AgencyDetailSection>('section', {
    required: true
  })

  const emit = defineEmits<{
    (e: 'select', agencyId: string): void
  }>()

  const service = useAgencyContext()
  const agency = computed(() => service?.agency.value)

  const sidebarItems = getAgencyDetailSidebarItems()

  const headerTitle = computed(() => agency.value?.name ?? 'Agency')

  function onSelect(agencyId: string | undefined) {
    if (agencyId) {
      emit('select', agencyId)
    }
  }

  const agencyModalProviderRef = useTemplateRef<
    InstanceType<typeof AgencyModalProvider>
  >('agencyModalProviderRef')

  // Viewport-filling mode, toggled from the header next to the close button.
  // Kept across open/close within the session (the modal root stays mounted),
  // but not persisted. Mirrors TenantUserDetailModal.
  const isFullScreen = ref(false)

  // Overrides on top of sidebarMenuButtonVariants to match the user-settings
  // modal styling (auto height, muted inactive text). ring-inset: the sidebar
  // content scrolls, which would clip a ring drawn outside the button bounds.
  const menuButtonClass =
    'text-muted-foreground hover:bg-accent/50 hover:text-foreground data-[active=true]:hover:bg-accent data-[active=true]:hover:text-accent-foreground h-auto cursor-pointer px-3 py-2 focus-visible:ring-inset'

  /** Mobile tab-bar item classes. */
  function tabClass(value: AgencyDetailSection) {
    return cn(
      'flex cursor-pointer items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm transition-colors',
      activeSection.value === value
        ? 'border-primary text-foreground font-medium'
        : 'text-muted-foreground hover:text-foreground hover:border-border border-transparent'
    )
  }
</script>

<template>
  <Modal
    v-model:open="open"
    type="dialog"
    :max-width="1000"
    full-screen-mobile
    hide-close-button
    :full-screen="isFullScreen"
  >
    <AgencyModalProvider ref="agencyModalProviderRef">
      <SidebarProvider
        :class="
          cn(
            'h-full min-h-0 w-full overflow-hidden',
            isFullScreen ? 'md:h-full' : 'md:h-[min(800px,85vh)] md:rounded-lg'
          )
        "
      >
        <ResizablePanelGroup
          direction="horizontal"
          auto-save-id="agency-modal-sidebar"
        >
          <ResizablePanel
            class="hidden md:block"
            :default-size="22"
            :min-size="18"
            :max-size="40"
          >
            <Sidebar
              collapsible="none"
              class="bg-muted/50 hidden h-full w-full p-3 dark:bg-neutral-800/50 md:flex"
            >
              <SidebarContent class="gap-3">
                <SidebarGroup class="p-0">
                  <SidebarGroupContent>
                    <SidebarHeader class="px-1 py-1 text-sm font-medium">
                      Agency
                    </SidebarHeader>
                    <ButtonGroup class="w-full">
                      <AgencySelect
                        :model-value="agencyId"
                        :pre-selected-agency="agency"
                        required
                        input-class="rounded-r-none border-r-0"
                        class="grow"
                        @update:model-value="onSelect"
                      />
                      <ButtonGroupSeparator />
                      <DropdownMenu v-if="agency">
                        <DropdownMenuTrigger as-child>
                          <Button
                            variant="outline"
                            size="icon"
                            :data-testid="testIds.agency.layout.actionsDropdown"
                            class="shrink-0 rounded-l-none shadow-none"
                          >
                            <IconDotsHorizontal class="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            v-if="agency && agency.canChangeName"
                            :data-testid="testIds.agency.action.changeName"
                            @click="
                              agencyModalProviderRef?.showChangeAgencyNameModal(
                                {
                                  agency
                                }
                              )
                            "
                          >
                            Change Name
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </ButtonGroup>
                  </SidebarGroupContent>
                </SidebarGroup>

                <SidebarGroup class="p-0">
                  <SidebarGroupContent>
                    <SidebarMenu>
                      <SidebarMenuItem
                        v-for="item in sidebarItems"
                        :key="item.key"
                      >
                        <SidebarMenuButton
                          :is-active="activeSection === item.key"
                          :class="menuButtonClass"
                          @click="activeSection = item.key"
                        >
                          <component :is="item.icon" />
                          <span>{{ item.label }}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
                <div
                  v-if="agency?.isInactive"
                  class="mt-auto flex flex-col px-1"
                >
                  <div
                    class="bg-background shadow-xs ring-border flex w-full shrink-0 grow-0 flex-col gap-2 self-start rounded-sm p-2.5 text-xs ring-1 ring-inset"
                  >
                    <div class="flex flex-wrap items-center gap-2 font-medium">
                      <IconBan class="text-destructive size-4 rounded-full" />
                      Deactivated
                    </div>
                    <Separator />
                    <div class="text-muted-foreground">
                      <span> This agency is deactivated permanently. </span>
                      <br /><br />
                      You can activate the agency and read more about agency
                      activation on the
                      <button
                        type="button"
                        class="text-link cursor-pointer"
                        @click="activeSection = 'danger-zone'"
                      >
                        danger zone
                      </button>
                      page.
                    </div>
                  </div>
                </div>
              </SidebarContent>
            </Sidebar>
          </ResizablePanel>
          <ResizableHandle class="hidden md:flex" />
          <ResizablePanel class="flex flex-col" :default-size="78">
            <SidebarInset class="overflow-hidden">
              <div class="relative z-10 shrink-0">
                <div class="bg-background flex items-center px-3 py-1.5">
                  <div class="min-w-0 flex-1">
                    <span
                      class="text-foreground truncate pl-1 text-sm font-semibold md:hidden"
                    >
                      {{ headerTitle }}
                    </span>
                  </div>
                  <button
                    type="button"
                    class="text-muted-foreground hover:text-foreground hover:bg-accent hidden shrink-0 cursor-pointer rounded-md p-1.5 transition-colors md:block"
                    @click="isFullScreen = !isFullScreen"
                  >
                    <IconMinimize v-if="isFullScreen" class="size-4" />
                    <IconMaximize v-else class="size-4" />
                    <span class="sr-only">
                      {{ isFullScreen ? 'Exit full screen' : 'Full screen' }}
                    </span>
                  </button>
                  <button
                    type="button"
                    class="text-muted-foreground hover:text-foreground hover:bg-accent shrink-0 cursor-pointer rounded-md p-1.5 transition-colors"
                    @click="open = false"
                  >
                    <IconClose class="size-4" />
                    <span class="sr-only">Close</span>
                  </button>
                </div>
                <nav
                  class="border-border bg-background flex shrink-0 gap-1 overflow-x-auto border-b px-3 md:hidden"
                >
                  <button
                    v-for="item in sidebarItems"
                    :key="item.key"
                    type="button"
                    :class="tabClass(item.key)"
                    @click="activeSection = item.key"
                  >
                    <component :is="item.icon" class="size-4" />
                    <span>{{ item.label }}</span>
                  </button>
                </nav>
                <div
                  class="bg-background pointer-events-none h-3"
                  style="
                    mask-image: linear-gradient(to bottom, black, transparent);
                  "
                />
              </div>
              <div class="-mt-3 flex flex-1 flex-col gap-6 overflow-y-auto p-5">
                <template v-if="agencyId">
                  <AgencyGeneralView
                    v-if="activeSection === 'general'"
                    :id="agencyId"
                  />
                  <AgencyPortsView
                    v-else-if="activeSection === 'ports'"
                    :id="agencyId"
                  />
                  <AgencyDangerZoneView
                    v-else-if="activeSection === 'danger-zone'"
                    :id="agencyId"
                  />
                </template>
                <div
                  v-else
                  class="text-muted-foreground flex flex-1 items-center justify-center p-8 text-center text-sm"
                >
                  Select an agency to view its details.
                </div>
              </div>
            </SidebarInset>
          </ResizablePanel>
        </ResizablePanelGroup>
      </SidebarProvider>
    </AgencyModalProvider>
  </Modal>
</template>
